import { useState, useRef, useCallback, useEffect } from "react";
import { supabase } from "../../lib/supabase";

interface VideoUploaderProps {
  value: string;
  onChange: (url: string) => void;
  label?: string;
  className?: string;
}

type VideoMode = "upload" | "url";

export default function VideoUploader({
  value,
  onChange,
  label = "Video de la noticia",
  className = "",
}: VideoUploaderProps) {
  const [mode, setMode] = useState<VideoMode>(value && !value.includes("youtube") && !value.includes("youtu.be") && value.startsWith("http") ? "upload" : "url");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(0);
  const [urlInput, setUrlInput] = useState(
    value && (value.includes("youtube") || value.includes("youtu.be") || !value.startsWith("http")) ? value : ""
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const classifyError = (msg: string): string => {
    const lower = msg.toLowerCase();
    if (lower.includes("bucket") || lower.includes("not found") || lower.includes("storage")) {
      return "El almacenamiento de videos no esta configurado. Contacta al soporte.";
    }
    if (lower.includes("rls") || lower.includes("policy") || lower.includes("permission") || lower.includes("unauthorized")) {
      return "No tienes permiso para subir videos. Verifica tu sesion.";
    }
    if (lower.includes("network") || lower.includes("failed to fetch")) {
      return "Error de conexion. Verifica tu internet.";
    }
    if (lower.includes("413") || lower.includes("too large") || lower.includes("50 mb")) {
      return "El video es muy grande. Maximo 50 MB. Intenta comprimirlo o usa una URL de YouTube.";
    }
    if (lower.includes("video/")) {
      return "Formato no valido. Solo se permiten videos MP4, WebM o MOV.";
    }
    return msg;
  };

  const uploadVideo = async (file: File) => {
    setError("");
    setUploading(true);
    setProgress(10);

    try {
      const timestamp = Date.now();
      const random = Math.random().toString(36).substring(2, 8);
      const ext = file.name.split(".").pop() || "mp4";
      const fileName = `${timestamp}-${random}.${ext}`;

      setProgress(30);

      const formData = new FormData();
      formData.append("file", file, fileName);
      formData.append("fileName", fileName);

      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/upload-video`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${anonKey}`,
        },
        body: formData,
      });

      setProgress(70);

      let result: { url?: string; error?: string } = {};
      const text = await response.text();
      try {
        result = JSON.parse(text);
      } catch {
        throw new Error(`Servidor respondio ${response.status}: ${text.slice(0, 200)}`);
      }

      if (!response.ok || result.error) {
        throw new Error(result.error || `Error del servidor (${response.status})`);
      }

      if (!result.url) {
        throw new Error("La subida se completo pero no se recibio la URL del video.");
      }

      setProgress(100);
      onChangeRef.current(result.url);
      setUrlInput("");
    } catch (err) {
      const rawMsg = err instanceof Error ? err.message : "Error al subir el video.";
      setError(classifyError(rawMsg));
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 500);
    }
  };

  const handleFile = (file: File) => {
    if (!file.type.startsWith("video/")) {
      setError("Solo se permiten archivos de video (MP4, WebM, MOV).");
      return;
    }
    const MAX_SIZE = 50 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      setError("El video excede 50 MB. Comprímelo antes de subir, o usa una URL de YouTube.");
      return;
    }
    uploadVideo(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleUrlConfirm = () => {
    const trimmed = urlInput.trim();
    if (!trimmed) {
      setError("Ingresa una URL valida.");
      return;
    }
    if (!trimmed.startsWith("http")) {
      setError("La URL debe comenzar con http:// o https://");
      return;
    }
    setError("");
    onChangeRef.current(trimmed);
  };

  const handleRemove = useCallback(() => {
    onChangeRef.current("");
    setUrlInput("");
    setError("");
  }, []);

  const isVideoUrl = value && value.startsWith("http") && !value.includes("youtube") && !value.includes("youtu.be");

  return (
    <div className={className}>
      {label && (
        <label className="block text-[#0B1F3A] font-semibold text-xs mb-2 uppercase tracking-wide">
          {label} <span className="text-slate-400 normal-case">(opcional)</span>
        </label>
      )}

      {/* Toggle mode */}
      <div className="flex gap-1 mb-3 bg-slate-100 p-1 rounded-lg w-fit">
        <button
          type="button"
          onClick={() => { setMode("upload"); setError(""); }}
          className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all cursor-pointer whitespace-nowrap ${
            mode === "upload"
              ? "bg-white text-[#0B1F3A] shadow-sm"
              : "text-slate-500 hover:text-slate-700"
          }`}
        >
          <i className="ri-upload-cloud-line mr-1" /> Subir archivo
        </button>
        <button
          type="button"
          onClick={() => { setMode("url"); setError(""); }}
          className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all cursor-pointer whitespace-nowrap ${
            mode === "url"
              ? "bg-white text-[#0B1F3A] shadow-sm"
              : "text-slate-500 hover:text-slate-700"
          }`}
        >
          <i className="ri-link mr-1" /> Pegar URL
        </button>
      </div>

      {/* Already uploaded preview */}
      {value && (
        <div className="mb-3 rounded-xl overflow-hidden border border-slate-200 bg-[#0B1F3A]">
          {isVideoUrl ? (
            <video
              src={value}
              controls
              className="w-full aspect-video object-contain bg-black"
              preload="metadata"
            />
          ) : value.includes("youtube") || value.includes("youtu.be") ? (
            <div className="w-full aspect-video bg-slate-900 flex items-center justify-center">
              <div className="text-center">
                <i className="ri-youtube-line text-red-500 text-4xl mb-2" />
                <p className="text-white text-xs">Video de YouTube</p>
                <p className="text-slate-400 text-[10px] truncate max-w-[260px] mx-auto mt-1">{value}</p>
              </div>
            </div>
          ) : null}
          <div className="px-3 py-2 flex items-center justify-between bg-white border-t border-slate-100">
            <span className="text-[10px] text-slate-400 truncate max-w-[200px]">{value}</span>
            <button
              type="button"
              onClick={handleRemove}
              className="w-7 h-7 flex items-center justify-center bg-red-50 hover:bg-red-100 text-red-500 rounded-lg transition-colors cursor-pointer"
              title="Eliminar video"
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>
        </div>
      )}

      {/* Upload mode */}
      {mode === "upload" && !value && (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          className="relative rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 hover:border-slate-400 hover:bg-slate-100 transition-all cursor-pointer p-6 text-center"
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <i className="ri-loader-4-line animate-spin text-[#0B1F3A] text-2xl" />
              <p className="text-slate-500 text-xs font-medium">
                {progress < 30 ? "Preparando subida..." : progress < 70 ? "Subiendo al servidor..." : "Finalizando..."}
              </p>
              <div className="w-full max-w-[200px] h-1.5 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#0B1F3A] rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl border border-slate-200">
                <i className="ri-movie-line text-slate-400 text-xl" />
              </div>
              <p className="text-[#0B1F3A] text-sm font-semibold">
                Arrastra un video aqui o haz clic para seleccionar
              </p>
              <p className="text-slate-500 text-xs font-medium">
                MP4, WebM, MOV · Max. 50 MB
              </p>
              <p className="text-slate-400 text-[10px]">
                Tip: usa un video ya comprimido (H.264) para mejor rendimiento.
              </p>
            </div>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="video/mp4,video/webm,video/quicktime"
            onChange={handleInputChange}
            className="hidden"
          />
        </div>
      )}

      {/* URL mode */}
      {mode === "url" && !value && (
        <div className="space-y-2">
          <input
            type="text"
            value={urlInput}
            onChange={(e) => { setUrlInput(e.target.value); setError(""); }}
            placeholder="https://www.youtube.com/watch?v=..."
            className="w-full px-4 py-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]/30 focus:border-[#0B1F3A] transition-all"
          />
          <button
            type="button"
            onClick={handleUrlConfirm}
            className="px-4 py-2 bg-[#0B1F3A] hover:bg-slate-800 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-check-line mr-1" /> Usar esta URL
          </button>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 mt-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          <i className="ri-error-warning-line text-red-500 text-xs mt-0.5 flex-shrink-0" />
          <p className="text-red-600 text-xs leading-relaxed">{error}</p>
        </div>
      )}
    </div>
  );
}